import React, { FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useSwipeable } from 'react-swipeable';
import { useDeviceHandler } from '~Components/DeviceProvider';
import { Box } from '~Styles';
import { useI18n } from '~Hooks';
import { SETTINGS_ROW_MIN_HEIGHT } from '~ComponentsImproved/settingsRowMinHeight';

const toggleHeight = 25;
const toggleWidth = 50;
const circleSize = toggleHeight;

const Wrapper = styled.div<{ fullWidth: boolean; compact: boolean }>`
  display: inline-block;
  /* The tablet row height would otherwise show as empty space under short handheld rows */
  min-height: ${({ compact }) => (compact ? 'auto' : `${SETTINGS_ROW_MIN_HEIGHT}px`)};
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
  box-sizing: border-box;
`;

const Item = styled.div<{
  onClick?: () => void;
  disabled: boolean;
  darkBackground: boolean;
  fullWidth: boolean;
  compact: boolean;
}>`
  display: flex;
  align-items: center;
  position: relative;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  justify-content: space-between;
  background-color: ${({ darkBackground }) =>
    darkBackground ? 'var(--colorBlueGray1100)' : 'transparent'};
  padding: ${({ darkBackground, compact }) =>
    darkBackground ? (compact ? '8px 8px' : '12px 16px') : '0'};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`;

const Text = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-right: 16px;
`;

const ToggleWrapper = styled.div<{
  active: boolean;
  darkBackground: boolean;
}>`
  width: ${toggleWidth}px;
  min-width: ${toggleWidth}px;
  height: ${toggleHeight}px;
  border-radius: 15px;
  background: ${({ active, darkBackground }) =>
    active ? '#6aa84f' : darkBackground ? 'var(--colorBlueGray1000)' : 'var(--colorBlueGray400)'};
  border: solid 1px var(--colorBlueGray400);
  position: relative;
  transition: all 450ms cubic-bezier(0.23, 1, 0.32, 1);
  cursor: pointer;
`;

const ToggleCircle = styled.div<{
  active: boolean;
}>`
  width: ${circleSize}px;
  height: ${circleSize}px;
  background: var(--colorWhite);
  border-radius: 50%;
  position: absolute;
  left: ${({ active }) => (active ? `${toggleWidth - circleSize}px` : 0)};
  transition: all 450ms cubic-bezier(0.23, 1, 0.32, 1);
  border: solid 1px var(--colorBlueGray100);
  box-sizing: border-box;
`;

export interface Props {
  label?: string;
  description?: string | JSX.Element;
  initialValue?: boolean;
  onToggle: (isActive: boolean) => void;
  disabled?: boolean;
  lightText?: boolean;
  darkBackground?: boolean;
  fullWidth?: boolean;
  /** If provided, this component is rendered in Settings and we need to check if it's remotely controlled */
  settingKey?: string;
  /** Tighter padding for handheld screens */
  compact?: boolean;
}

const Toggle: FC<Props> = ({
  label,
  description,
  initialValue,
  onToggle,
  disabled = false,
  lightText = false,
  darkBackground = false,
  fullWidth = false,
  settingKey,
  compact = false,
}) => {
  const { i18n } = useI18n();
  const deviceHandler = useDeviceHandler();
  const isRemotelyControlled = settingKey && deviceHandler.remotelyControlledData[settingKey];

  const [isScrolling, setIsScrolling] = useState<boolean>(false);
  const [isActive, setIsActive] = useState(initialValue ?? false);

  const textColor = lightText || darkBackground ? 'var(--colorWhite)' : 'var(--colorBlack)';
  const descriptionTextColor =
    lightText || darkBackground ? 'var(--colorBlueGray300)' : 'var(--colorBlack)';

  const isDisabled = disabled || Boolean(isRemotelyControlled);

  const handlers = useSwipeable({
    delta: 10,
    onSwiping: () => {
      setIsScrolling(true);
    },
  });

  let timeout: ReturnType<typeof setTimeout>;
  const onTouchEnd = () => {
    if (!isScrolling && !isDisabled) {
      setIsActive(!isActive);
      onToggle(!isActive);
    }

    timeout = setTimeout(() => {
      setIsScrolling(false);
    }, 100);
  };

  return (
    <Wrapper fullWidth={fullWidth} compact={compact} {...handlers}>
      <Item
        onTouchEnd={onTouchEnd}
        disabled={disabled || Boolean(isRemotelyControlled)}
        darkBackground={darkBackground}
        fullWidth={fullWidth}
        compact={compact}>
        {label && (
          <Text>
            <Heading level={5} color={textColor}>
              {label}
            </Heading>
            {description && (
              <Paragraph level={5} color={descriptionTextColor}>
                {description}
              </Paragraph>
            )}
          </Text>
        )}

        <Box display="flex" alignItems="center" gap="xs">
          {Boolean(isRemotelyControlled) && (
            <Paragraph level={5} color={descriptionTextColor}>
              ({i18n('Settings.LockedByBaemingo')})
            </Paragraph>
          )}
          <ToggleWrapper active={isActive} darkBackground={darkBackground}>
            <ToggleCircle active={isActive} />
          </ToggleWrapper>
        </Box>
      </Item>
    </Wrapper>
  );
};

export default React.memo(Toggle);
